import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { B, PILLARS, industryCallouts } from "../data/pillars";
import { getRiskLevel, getRecs } from "../data/recommendations";
import Gauge from "../components/Gauge";
import PillarBar from "../components/PillarBar";
import MaturityRoadmap from "../components/MaturityRoadmap";
import SectionHeader from "../components/SectionHeader";
import ServiceCard from "../components/ServiceCard";
import RecCard from "../components/RecCard";
import CostOfInaction from "../components/CostOfInaction";
import EbitdaImpact from "../components/EbitdaImpact";
import UrgencyMatrix from "../components/UrgencyMatrix";
import BenchmarkBanner from "../components/BenchmarkBanner";
import RiskExposurePanel from "../components/RiskExposurePanel";
import EdgeCheckCTA from "../components/EdgeCheckCTA";
import ScoreHeatmap from "../components/ScoreHeatmap";
import PillarDeepDive from "../components/PillarDeepDive";
import ExecutiveSnapshot from "../components/ExecutiveSnapshot";

const NAV_SECTIONS = [
  { id: "executive", label: "Executive Summary", icon: "📊" },
  { id: "heatmap", label: "Score Heatmap", icon: "🗺️" },
  { id: "risk", label: "Risk Exposure", icon: "⚠️" },
  { id: "pillars", label: "Pillar Deep Dive", icon: "🔍" },
  { id: "priority", label: "Priority Matrix", icon: "🚨" },
  { id: "recommendations", label: "Recommendations", icon: "💡" },
  { id: "ai", label: "AI Readiness", icon: "🤖" },
  { id: "coi", label: "Cost of Inaction", icon: "💰" },
  { id: "ebitda", label: "EBITDA Impact", icon: "📈" },
  { id: "next", label: "Next Steps", icon: "🎯" },
];

export default function ReportPage({ intake, answers, confidenceMap, pillarScores, onEdit }) {
  const reportRef = useRef(null);
  const [activeSection, setActiveSection] = useState("executive");
  const [navOpen, setNavOpen] = useState(false);

  const avgScore = Object.values(pillarScores).reduce((a, b) => a + b, 0) / PILLARS.length;
  const risk = getRiskLevel(avgScore);
  const recs = getRecs(pillarScores, intake.vertical);
  const targetScore = Math.min(avgScore + 2.5, 4.5);
  const months = avgScore < 2 ? 18 : avgScore < 3 ? 12 : 9;
  const unsureCount = Object.values(confidenceMap).filter(v => v === false).length;

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setActiveSection(id);
    setNavOpen(false);
  };

  return (
    <div ref={reportRef} style={{
      minHeight: "100vh", background: B.navy,
      fontFamily: "'Segoe UI',system-ui,sans-serif", color: B.white,
    }}>
      {/* TOP NAV */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "12px 24px", borderBottom: "1px solid #1E3A5F",
        position: "sticky", top: 0, background: B.navy, zIndex: 100,
        flexWrap: "wrap", gap: 10,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={onEdit} style={{
            background: "none", border: "1px solid #1E3A5F", borderRadius: 8,
            color: B.subText, cursor: "pointer", fontSize: 12, padding: "6px 12px", fontWeight: 600,
          }}>← Edit</button>
          <div style={{
            background: B.blue, color: B.white, fontWeight: 800,
            fontSize: 11, padding: "5px 12px", borderRadius: 5, letterSpacing: 1,
          }}>VISUAL EDGE IT</div>
          {unsureCount > 0 && (
            <div style={{
              background: B.yellow + "20", border: `1px solid ${B.yellow}40`,
              color: B.yellow, fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 6,
            }}>
              ⚠ {unsureCount} item{unsureCount > 1 ? "s" : ""} need validation
            </div>
          )}
        </div>

        {/* Section Nav Pills */}
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", flex: 1, justifyContent: "center" }}>
          {NAV_SECTIONS.map(s => (
            <button key={s.id} onClick={() => scrollTo(s.id)} style={{
              background: activeSection === s.id ? B.blue + "30" : "none",
              border: `1px solid ${activeSection === s.id ? B.blue : "#1E3A5F"}`,
              borderRadius: 6, padding: "5px 10px",
              color: activeSection === s.id ? B.accent : B.subText,
              fontSize: 11, fontWeight: 600, cursor: "pointer",
              whiteSpace: "nowrap", transition: "all 0.15s",
            }}>
              {s.icon} {s.label}
            </button>
          ))}
        </div>

        <button onClick={() => window.print()} style={{
          background: `linear-gradient(135deg,${B.blue},${B.darkBlue})`,
          border: "none", borderRadius: 8, padding: "8px 18px",
          color: B.white, fontWeight: 700, fontSize: 12, cursor: "pointer",
        }}>↓ Export PDF</button>
      </div>

      <div style={{ maxWidth: 960, margin: "0 auto", padding: "32px 20px 80px" }}>

        {/* COVER CARD */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          style={{
            background: `linear-gradient(135deg,#1A2E4A,#0D1B2E)`,
            borderRadius: 20, border: "1px solid #1E3A5F",
            padding: "clamp(24px,5vw,44px)", marginBottom: 28,
            position: "relative", overflow: "hidden",
          }}
        >
          {/* Background accent */}
          <div style={{
            position: "absolute", top: -60, right: -60, width: 240, height: 240,
            borderRadius: "50%", background: risk.color + "08", pointerEvents: "none",
          }} />
          <div style={{
            position: "absolute", bottom: -40, left: -40, width: 160, height: 160,
            borderRadius: "50%", background: B.blue + "08", pointerEvents: "none",
          }} />

          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
            <div style={{
              background: B.blue, color: B.white, fontWeight: 800,
              fontSize: 11, padding: "5px 12px", borderRadius: 5, letterSpacing: 1,
            }}>VISUAL EDGE IT</div>
            <div style={{ color: B.subText, fontSize: 11, letterSpacing: 1 }}>TECHNOLOGY MATURITY ASSESSMENT</div>
            <div style={{ marginLeft: "auto", color: B.subText, fontSize: 11 }}>{new Date().toLocaleDateString()}</div>
          </div>

          <h1 style={{
            fontSize: "clamp(26px,5vw,44px)", fontWeight: 900,
            margin: "0 0 4px", lineHeight: 1.1, wordBreak: "break-word",
          }}>{intake.company}</h1>
          <h2 style={{
            fontSize: "clamp(18px,3vw,26px)", fontWeight: 700,
            margin: "0 0 28px", color: B.accent, lineHeight: 1.2,
          }}>Technology Maturity Roadmap</h2>

          {/* Meta grid */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(120px,1fr))",
            gap: 16, marginBottom: 28,
          }}>
            {[
              { label: "Industry", val: intake.vertical || "—" },
              { label: "Employees", val: intake.employees || "—" },
              { label: "Devices", val: intake.devices || "—" },
              { label: "OML Score", val: avgScore.toFixed(1), color: risk.color, large: true },
              { label: "Risk Level", val: risk.label, color: risk.color },
              ...(intake.rep ? [{ label: "VEIT Rep", val: intake.rep }] : []),
            ].map(f => (
              <div key={f.label} style={{
                background: "#122240", borderRadius: 10, padding: "12px 16px",
                border: f.large ? `1px solid ${risk.color}40` : "1px solid #1E3A5F",
              }}>
                <div style={{ fontSize: 10, color: B.subText, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 6 }}>{f.label}</div>
                <div style={{ fontSize: f.large ? 28 : 15, fontWeight: f.large ? 900 : 700, color: f.color || B.white }}>{f.val}</div>
              </div>
            ))}
          </div>

          {/* Pillar score mini-bars */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 10 }}>
            {PILLARS.map(p => {
              const s = pillarScores[p.id];
              const pct = (s / 5) * 100;
              return (
                <div key={p.id} style={{ background: "#0D1B2E", borderRadius: 8, padding: "10px 12px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                    <span style={{ fontSize: 12, color: B.lightGray, fontWeight: 600 }}>{p.icon} {p.label}</span>
                    <span style={{ fontSize: 12, color: p.color, fontWeight: 800 }}>{s.toFixed(1)}</span>
                  </div>
                  <div style={{ background: "#1E3A5F", borderRadius: 4, height: 5, overflow: "hidden" }}>
                    <motion.div
                      initial={{ width: 0 }} animate={{ width: pct + "%" }}
                      transition={{ duration: 1, ease: "easeOut" }}
                      style={{ height: "100%", background: p.color, borderRadius: 4 }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* BENCHMARK BANNER */}
        <BenchmarkBanner vertical={intake.vertical} avgScore={avgScore} />

        {/* SECTION 1 — EXECUTIVE SUMMARY */}
        <div id="executive">
          <ExecutiveSnapshot
            intake={intake} avgScore={avgScore} risk={risk}
            pillarScores={pillarScores} targetScore={targetScore}
            months={months} answers={answers} confidenceMap={confidenceMap}
          />
        </div>

        {/* SECTION 2 — SCORE HEATMAP */}
        <div id="heatmap">
          <ScoreHeatmap
            answers={answers} pillarScores={pillarScores}
            confidenceMap={confidenceMap}
          />
        </div>

        {/* SECTION 3 — RISK EXPOSURE */}
        <div id="risk" style={{
          background: "#1A2E4A", borderRadius: 16, border: "1px solid #1E3A5F",
          padding: "clamp(20px,4vw,32px)", marginBottom: 24, boxSizing: "border-box",
        }}>
          <SectionHeader num={3} title="Risk Exposure Analysis" sub="Composite Risk Index by Domain" />
          <RiskExposurePanel avgScore={avgScore} pillarScores={pillarScores} employees={intake.employees} />
        </div>

        {/* SECTION 4 — PILLAR DEEP DIVE */}
        <div id="pillars">
          <PillarDeepDive
            answers={answers} pillarScores={pillarScores}
            confidenceMap={confidenceMap} intake={intake}
          />
        </div>

        {/* SECTION 5 — PRIORITY MATRIX */}
        <div id="priority">
          <UrgencyMatrix answers={answers} pillarScores={pillarScores} confidenceMap={confidenceMap} />
        </div>

        {/* SECTION 6 — RECOMMENDATIONS */}
        <div id="recommendations" style={{
          background: "#1A2E4A", borderRadius: 16, border: "1px solid #1E3A5F",
          padding: "clamp(20px,4vw,32px)", marginBottom: 24, boxSizing: "border-box",
        }}>
          <SectionHeader num={6} title="Strategic Recommendations" sub="90-Day Remediation Plan" />
          <p style={{ color: B.lightGray, fontSize: 14, lineHeight: 1.7, marginBottom: 20 }}>
            Recommendations are ranked by urgency and weighted by pillar risk contribution.
            Each item maps directly to a Visual Edge IT service capability.
          </p>
          {recs.length === 0
            ? <div style={{ color: B.lightGray, fontSize: 14, padding: 20, textAlign: "center" }}>
                Strong posture — continue with vCIO advisory for optimization planning.
              </div>
            : recs.map((r, i) => <RecCard key={i} num={i + 1} rec={r} pillarScore={pillarScores[r.pillar]} />)
          }
        </div>

        {/* SECTION 7 — VEIT SERVICES */}
        <div style={{
          background: "#1A2E4A", borderRadius: 16, border: "1px solid #1E3A5F",
          padding: "clamp(20px,4vw,32px)", marginBottom: 24, boxSizing: "border-box",
        }}>
          <SectionHeader num={7} title="The VEIT Advantage" sub="Integrated Solutions to Bridge Your Maturity Gaps" />
          <p style={{ color: B.lightGray, fontSize: 14, lineHeight: 1.7, marginBottom: 24 }}>
            Visual Edge IT provides an integrated ecosystem of services designed to move clients up the OML framework.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 16 }}>
            <ServiceCard icon="⚙️" title="Managed IT Services" sub="Proactive NOC & Help Desk" subColor={B.green}
              desc="We replace reactive maintenance with 24/7/365 RMM. Our US-based Help Desk provides immediate relief while our NOC ensures patches and updates are applied during off-hours."
              tags={["24/7/365 Monitoring", "Unlimited Help Desk", "Automated Patch Mgmt", "Asset Lifecycle"]} />
            <ServiceCard icon="🔒" title="Cybersecurity & Compliance" sub="Defense-in-Depth Architecture" subColor={B.red}
              desc="We move you from basic antivirus to an enterprise-grade security stack. MDR/XDR with human analyst oversight, MFA for identity protection, and continuous vulnerability scanning."
              tags={["MDR/XDR + Human SOC", "Multi-Factor Auth", "Phishing Simulation", "SOC-as-a-Service"]} />
            <ServiceCard icon="🖨️" title="Managed Print & Document" sub="Fleet Optimization & Digital Workflow" subColor={B.orange}
              desc="We eliminate hidden print costs. Our MPS solution automates supply fulfillment while our DMS platform digitizes paper workflows."
              tags={["Zero-Touch Supply", "Secure Badge-Release Print", "Digital Doc Archiving", "AP/AR Automation"]} />
            <ServiceCard icon="🤖" title="AI & Process Automation" sub="Intelligence-First Operations" subColor="#9B59B6"
              desc="We prepare your organization for the AI-first economy. Our AI Launchpad deploys M365 Copilot, builds Power Automate workflows, and establishes data governance."
              tags={["M365 Copilot Deploy", "Power Automate Builds", "AI Governance Policy", "Copilot Readiness"]} />
            <ServiceCard icon="☁️" title="Cloud Modernization" sub="Cloud-First Infrastructure" subColor={B.blue}
              desc="We migrate your infrastructure to cloud-first architecture. From M365 optimization to Azure deployment, we eliminate on-prem technical debt."
              tags={["M365 Migration", "Azure Infrastructure", "SharePoint Build-Out", "Cloud Security Baseline"]} />
            <ServiceCard icon="📊" title="vCIO & Strategic Advisory" sub="Fractional Executive Leadership" subColor={B.teal}
              desc="Our vCIOs conduct Quarterly Business Reviews to align IT spend with your revenue goals and build a multi-year technology roadmap."
              tags={["Quarterly Business Reviews", "IT Budget Optimization", "Compliance Roadmapping", "Tech Lifecycle Planning"]} />
          </div>
        </div>

        {/* SECTION 8 — AI READINESS */}
        <div id="ai" style={{
          background: "#1A2E4A", borderRadius: 16, border: "1px solid #9B59B640",
          padding: "clamp(20px,4vw,32px)", marginBottom: 24, boxSizing: "border-box",
        }}>
          <SectionHeader num={8} title="AI & Automation Maturity" sub="Intelligence Readiness Assessment" />
          <div style={{ display: "flex", flexWrap: "wrap", gap: 24, alignItems: "flex-start" }}>
            <Gauge score={pillarScores.ai} max={5} size={130} color="#9B59B6" />
            <div style={{ flex: "1 1 280px" }}>
              <p style={{ color: B.lightGray, fontSize: 14, lineHeight: 1.7, margin: "0 0 16px" }}>
                {pillarScores.ai < 2
                  ? `${intake.company} has no AI tools deployed and processes are largely undocumented.`
                  : pillarScores.ai < 3
                  ? `${intake.company} is exploring AI concepts but has not yet standardized tooling or governance.`
                  : `${intake.company} has piloted AI tools and is building toward an intelligence-first operation.`}
              </p>
              <div style={{ background: "#122240", borderRadius: 10, padding: "14px 18px" }}>
                <div style={{ fontSize: 11, color: "#9B59B6", fontWeight: 700, letterSpacing: 2, marginBottom: 10 }}>
                  AI READINESS GAPS
                </div>
                {PILLARS.find(p => p.id === "ai").questions.map(q => {
                  const score = (answers[q.id] || 0) + 1;
                  const isUnsure = confidenceMap[q.id] === false;
                  const c = score < 2 ? B.red : score < 3 ? B.orange : score < 4 ? B.yellow : B.green;
                  return (
                    <div key={q.id} style={{
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      padding: "8px 0", borderBottom: "1px solid #1E3A5F", gap: 8,
                    }}>
                      <span style={{ color: B.lightGray, fontSize: 13 }}>{q.label}</span>
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        {isUnsure && <span style={{ color: B.yellow, fontSize: 10, fontWeight: 700 }}>⚠ VALIDATE</span>}
                        <span style={{ color: c, fontSize: 13, fontWeight: 700 }}>OML {score}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 9 — COST OF INACTION */}
        <div id="coi">
          <CostOfInaction intake={intake} avgScore={avgScore} />
        </div>

        {/* SECTION 10 — EBITDA IMPACT */}
        <div id="ebitda">
          <EbitdaImpact avgScore={avgScore} />
        </div>

        {/* SECTION 11 — NEXT STEPS */}
        <div id="next">
          <EdgeCheckCTA intake={intake} avgScore={avgScore} />
        </div>

      </div>
    </div>
  );
}